"use client";

import { Logo } from "@/components/logo";
import { Locale } from "@/i18n";
import { cn } from "@/lib/utils";
import { ArrowLeft, ArrowRight, Facebook, Instagram, Mail, Twitter } from "lucide-react";
import Link from "next/link";
import { useState } from "react";


const translations: Record<Locale, {
  description: string;
  support: string;
  contact: string;
  faq: string;
  company: string;
  about: string;
  careers: string;
  newsletter: string;
  emailPlaceholder: string;
  subscribe: string;
  thankYou: string;
  rights: string;
  privacy: string;
  terms: string;
  explore: string;
  restaurants: string;
  discover: string;
}> = {
  en: {
    description: "Transforming ideas into digital reality. Join us on our journey to innovate and create amazing experiences.",
    support: "SUPPORT",
    contact: "Contact",
    faq: "FAQ",
    company: "COMPANY",
    about: "About",
    careers: "Careers",
    newsletter: "NEWSLETTER",
    emailPlaceholder: "Enter your email",
    subscribe: "Subscribe",
    thankYou: "Thanks for subscribing!",
    rights: "© 2024 All rights reserved.",
    privacy: "Privacy Policy",
    terms: "Terms of Use",
    explore: "EXPLORE",
    restaurants: "Restaurants",
    discover: "Discover"
  },
  fr: {
    description: "Transforming ideas into digital reality. Join us on our journey to innovate and create amazing experiences.",
    support: "SUPPORT",
    contact: "Contact",
    faq: "FAQ",
    company: "ENTREPRISE",
    about: "À propos",
    careers: "Carrières",
    newsletter: "NEWSLETTER",
    emailPlaceholder: "Entrez votre email",
    subscribe: "S'abonner",
    thankYou: "Merci de votre inscription!",
    rights: "© 2024 Tous droits réservés.",
    privacy: "Politique de confidentialité",
    terms: "Conditions d'utilisation",
    explore: "EXPLORER",
    restaurants: "Restaurants",
    discover: "Découvrir"
  },
  ar: {
    description: "نحول الأفكار إلى واقع رقمي. انضم إلينا في رحلتنا للابتكار وخلق تجارب مذهلة.",
    support: "الدعم",
    contact: "اتصل بنا",
    faq: "الأسئلة الشائعة",
    company: "الشركة",
    about: "عن الشركة",
    careers: "الوظائف",
    newsletter: "النشرة الإخبارية",
    emailPlaceholder: "أدخل بريدك الإلكتروني",
    subscribe: "اشترك",
    thankYou: "شكراً لاشتراكك!",
    rights: "© 2024 جميع الحقوق محفوظة.",
    privacy: "سياسة الخصوصية",
    terms: "شروط الاستخدام",
    explore: "استكشف",
    restaurants: "المطاعم",
    discover: "اكتشف"
  }
};

interface FooterProps {
  locale?: Locale;
}

export default function Footer({ locale }: FooterProps) {
  const [email, setEmail] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const t = translations[locale as Locale];
  const isRTL = locale === "ar";
  const Arrow = isRTL ? ArrowLeft : ArrowRight;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
    setEmail("");
    // Add newsletter subscription logic here
  };

  const socialLinks = [
    { icon: Facebook, href: "#", label: "Facebook" },
    { icon: Twitter, href: "#", label: "Twitter" },
    { icon: Instagram, href: "#", label: "Instagram" },
  ];

  const footerLinks = {
    explore: [
      { label: t.restaurants, href: "/search" as const },
      { label: t.discover, href: "/discover" as const },
    ],
    support: [
      { label: t.contact, href: "/contact" as const },
      { label: t.faq, href: "/faq" as const },
    ],
    company: [
      { label: t.about, href: "/about" as const },
      { label: t.careers, href: "/careers" as const },
      { label: t.privacy, href: "/privacy" as const },
      { label: t.terms, href: "/terms" as const },
    ]
  };

  return (
    <footer className={cn(
      "bg-slate-100 dark:bg-emerald-900",
      isRTL && "rtl"
    )}>
      <div className="mx-auto max-w-7xl px-6 pb-8 pt-16">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-4">
          {/* Brand Section */}
          <div className="space-y-6">
            <div className="group relative inline-block">
              <Logo />
            </div>
            <p className="max-w-xs text-sm text-emerald-600 dark:text-emerald-100/80">
              {t.description}
            </p>
            <div className="flex space-x-4">
              {socialLinks.map(({ icon: Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  className="text-emerald-500 transition-colors duration-300 hover:text-emerald-700 dark:text-emerald-300 dark:hover:text-emerald-100"
                  aria-label={label}
                >
                  <Icon size={20} />
                </a>
              ))}
            </div>
          </div>

          {/* Support Links Section */}
          <div>
            <h2 className="relative mb-6 text-lg font-bold text-emerald-700 dark:text-white">
              {t.support}
              <span className={cn(
                "absolute bottom-0 h-0.5 w-12 bg-emerald-500",
                isRTL ? "right-0" : "left-0"
              )}></span>
            </h2>
            <ul className="space-y-3">
              <li>
                <Link
                  href="/contact"
                  className="group flex items-center text-emerald-600 transition-colors duration-300 hover:text-emerald-700 dark:text-emerald-200 dark:hover:text-emerald-50"
                >
                  <Arrow className={cn(
                    "h-4 w-4 opacity-0 transition-all duration-300",
                    isRTL ? "-mr-4 group-hover:mr-0" : "-ml-4 group-hover:ml-0",
                    "group-hover:opacity-100"
                  )} />
                  <span>{t.contact}</span>
                </Link>
              </li>
              <li>
                <Link
                  href="/faq"
                  className="group flex items-center text-emerald-600 transition-colors duration-300 hover:text-emerald-700 dark:text-emerald-200 dark:hover:text-emerald-50"
                >
                  <Arrow className={cn(
                    "h-4 w-4 opacity-0 transition-all duration-300",
                    isRTL ? "-mr-4 group-hover:mr-0" : "-ml-4 group-hover:ml-0",
                    "group-hover:opacity-100"
                  )} />
                  <span>{t.faq}</span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Company Links Section */}
          <div>
            <h2 className="relative mb-6 text-lg font-bold text-emerald-700 dark:text-white">
              {t.company}
              <span className={cn(
                "absolute bottom-0 h-0.5 w-12 bg-emerald-500",
                isRTL ? "right-0" : "left-0"
              )}></span>
            </h2>
            <ul className="space-y-3">
              <li>
                <Link
                  href="/about"
                  className="group flex items-center text-emerald-600 transition-colors duration-300 hover:text-emerald-700 dark:text-emerald-200 dark:hover:text-emerald-50"
                >
                  <Arrow className={cn(
                    "h-4 w-4 opacity-0 transition-all duration-300",
                    isRTL ? "-mr-4 group-hover:mr-0" : "-ml-4 group-hover:ml-0",
                    "group-hover:opacity-100"
                  )} />
                  <span>{t.about}</span>
                </Link>
              </li>
              <li>
                <Link
                  href="/careers"
                  className="group flex items-center text-emerald-600 transition-colors duration-300 hover:text-emerald-700 dark:text-emerald-200 dark:hover:text-emerald-50"
                >
                  <Arrow className={cn(
                    "h-4 w-4 opacity-0 transition-all duration-300",
                    isRTL ? "-mr-4 group-hover:mr-0" : "-ml-4 group-hover:ml-0",
                    "group-hover:opacity-100"
                  )} />
                  <span>{t.careers}</span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Newsletter Section */}
          <div>
            <h2 className="relative mb-6 text-lg font-bold text-emerald-700 dark:text-white">
              {t.newsletter}
              <span className={cn(
                "absolute bottom-0 h-0.5 w-12 bg-emerald-500",
                isRTL ? "right-0" : "left-0"
              )}></span>
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <Mail
                  className={cn(
                    "absolute top-1/2 -translate-y-1/2 transform text-emerald-600 dark:text-emerald-300",
                    isRTL ? "right-3" : "left-3"
                  )}
                  size={20}
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t.emailPlaceholder}
                  className={cn(
                    "w-full rounded-lg bg-white dark:bg-emerald-800/30 py-3 text-emerald-700 dark:text-emerald-100 placeholder-emerald-500/80 dark:placeholder-emerald-300/70 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-emerald-500",
                    isRTL ? "pr-12 pl-4 text-right" : "pl-12 pr-4"
                  )}
                />
              </div>
              <button
                type="submit"
                className="w-full transform rounded-lg bg-emerald-600 px-6 py-3 text-white transition-all duration-300 hover:translate-y-[-2px] hover:bg-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2 focus:ring-offset-slate-100 dark:focus:ring-offset-emerald-900"
              >
                {isSubmitted ? t.thankYou : t.subscribe}
              </button>
            </form>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 border-t border-emerald-300/70 dark:border-emerald-600/50 pt-8">
          <div className="flex flex-col items-center justify-between space-y-4 md:flex-row md:space-y-0">
            <p className="text-sm text-emerald-500/90 dark:text-emerald-200/80">
              {t.rights}
            </p>
            <div className={cn(
              "flex",
              isRTL ? "space-x-reverse space-x-6" : "space-x-6"
            )}>
              <Link
                href="/privacy"
                className="text-sm text-emerald-500/90 transition-colors duration-300 hover:text-emerald-700 dark:text-emerald-200/80 dark:hover:text-emerald-50"
              >
                {t.privacy}
              </Link>
              <Link
                href="/terms"
                className="text-sm text-emerald-500/90 transition-colors duration-300 hover:text-emerald-700 dark:text-emerald-200/80 dark:hover:text-emerald-50"
              >
                {t.terms}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}